/*
 * Cart cross-sell quick-add (snippets/rb-cart-crosssell.liquid).
 *
 * Adds ONE line — on the selling plan named by `data-rb-quick-add-plan` when the
 * button carries one, one-time otherwise — then repaints the
 * sections that changed straight from the add response, so the drawer, the cart
 * bubble and (on the cart page) the lines and the totals reflect the new line
 * without a reload.
 *
 * On /cart there is no drawer to open: layout/theme.liquid renders <cart-drawer>
 * on every page, so opening it slid a copy of the cart over the cart the shopper
 * was already reading. The page repaints itself instead — and it does so from
 * THIS response, the way cart.js's own updateQuantity() does.
 *
 * It deliberately does NOT hand the visible result to CartItems.onCartUpdate().
 * That path is a second fetch whose failure cart.js only console.errors, so a
 * miss leaves the button reading "Adding…" and `aria-disabled` forever while the
 * bubble and the subtotal have already moved — and every later click then exits
 * at the aria-disabled guard without a request at all. On this store it misses
 * every time: cart.js hardcodes `?section_id=main-cart-items`, while
 * templates/cart.json names the section `cart-items`.
 *
 * The handler is delegated from `document`, because the drawer's markup is
 * replaced wholesale by CartDrawer.renderContents() and any listener bound to a
 * button inside it would die on the first refresh. The file is loaded from both
 * the drawer snippet and the cart-items section, so the flag guards the second
 * execution.
 */
(function () {
  if (window.rbCartQuickAdd) return;
  window.rbCartQuickAdd = true;

  var ADDING = 'Adding…';
  var BUBBLE = 'cart-icon-bubble';

  function el(id) {
    return document.getElementById(id);
  }

  function onCartPage() {
    return Boolean(el('main-cart-items')) || document.body.classList.contains('template-cart');
  }

  function drawer() {
    // Never on /cart: the cart page is the cart, and an overlay of it on top of
    // it is the interruption this add is supposed to avoid.
    if (onCartPage()) return null;
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  // A section's id is its key in the template JSON, not the name of its file:
  // this store's templates/cart.json calls them `cart-items` and `cart-footer`.
  // Each section prints its own id as `data-id`, which is where cart.js reads it
  // from too — hardcoding the file names is the bug described above.
  function sectionId(node) {
    return node && node.dataset.id ? node.dataset.id : null;
  }

  function swap(target, html, selector) {
    if (!target || !html) return;
    var source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (source) target.innerHTML = source.innerHTML;
  }

  function fail(button, message) {
    var box = button.closest('[data-rb-xsell]');
    var error = box && box.querySelector('[data-rb-xsell-error]');
    if (error) error.textContent = message;
  }

  function reset(button, label) {
    button.removeAttribute('aria-disabled');
    button.textContent = label;
  }

  function announce(state, source) {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    publish(PUB_SUB_EVENTS.cartUpdate, { source: source, cartData: state });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-rb-quick-add]');
    if (!button || button.getAttribute('aria-disabled') === 'true') return;

    event.preventDefault();

    var cart = drawer();
    var items = el('main-cart-items');
    var footer = el('main-cart-footer');
    var itemsId = sectionId(items);
    var footerId = sectionId(footer);
    var label = button.textContent;

    // With a drawer, it owns the repaint (its own markup plus the bubble).
    // Without one we are on the cart page and ask for its three sections.
    var ids = cart ? cart.getSectionsToRender().map(function (s) { return s.id; }) : [BUBBLE];
    if (!cart && itemsId) ids.push(itemsId);
    // The footer subscribes to nothing, so its subtotal is swapped by hand.
    if (footerId) ids.push(footerId);

    var body = new FormData();
    body.append('id', button.getAttribute('data-rb-quick-add'));
    body.append('quantity', '1');
    var plan = button.getAttribute('data-rb-quick-add-plan');
    if (plan) body.append('selling_plan', plan);
    body.append('sections', ids.join(','));
    body.append('sections_url', window.location.pathname);

    fail(button, '');
    button.setAttribute('aria-disabled', 'true');
    button.textContent = ADDING;
    if (cart && cart.setActiveElement) cart.setActiveElement(document.activeElement);

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: body,
    })
      .then(function (response) { return response.json(); })
      .then(function (state) {
        if (state.status) {
          fail(button, state.description || state.message);
          reset(button, label);
          return;
        }

        var sections = state.sections || {};

        if (footer) swap(footer.querySelector('.js-contents'), sections[footerId], '.js-contents');

        if (cart) {
          announce(state, 'rb-cart-crosssell');
          cart.renderContents(state);
          return;
        }

        // Cart page. `source: 'cart-items'` tells <cart-items> the repaint is
        // already done — the same claim updateQuantity() makes after it writes
        // its own sections — so nothing re-fetches behind us.
        swap(items && items.querySelector('.js-contents'), sections[itemsId], '.js-contents');
        swap(el(BUBBLE), sections[BUBBLE], '.shopify-section');
        announce(state, 'cart-items');
        // A no-op when the swap above replaced this button; the safety net for
        // when the section came back empty and the row is still on screen.
        reset(button, label);
      })
      .catch(function (error) {
        console.error(error);
        fail(button, error.message || 'Network error');
        reset(button, label);
      });
  });
})();
